import React, { useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import './VerseSearch.css';

function VerseSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchType, setSearchType] = useState('passage'); // 'passage' or 'word'
  const [copiedId, setCopiedId] = useState(null); // Track which verse was copied

  const API_KEY = process.env.REACT_APP_ESV_API_KEY;
  const PASSAGE_API_URL = 'https://api.esv.org/v3/passage/text/';
  const SEARCH_API_URL = 'https://api.esv.org/v3/passage/search/';

  // Fetch passage by reference
  const fetchPassageResults = async (query) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${PASSAGE_API_URL}?q=${encodeURIComponent(query)}&include-footnotes=false&include-headings=false`,
        {
          headers: {
            'Authorization': `Token ${API_KEY}`,
          },
        }
      );
      const data = await response.json();
      if (data.passages && data.passages.length > 0) {
        setSearchResults([
          {
            id: data.query,
            title: data.canonical,
            description: data.passages[0],
          },
        ]);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      setError('Failed to fetch passage results');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch verses containing a word or phrase
  const fetchWordResults = async (query) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${SEARCH_API_URL}?q=${encodeURIComponent(query)}&page-size=9999`,
        {
          headers: {
            'Authorization': `Token ${API_KEY}`,
          },
        }
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        setSearchResults(
          data.results.map((result) => ({
            id: result.reference,
            title: result.reference,
            description: result.content,
          }))
        );
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      setError('Failed to fetch word search results');
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.length > 2) {
        if (searchType === 'passage') {
          fetchPassageResults(query);
        } else {
          fetchWordResults(query);
        }
      } else {
        setSearchResults([]);
      }
    }, 500),
    [searchType]
  );

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const toggleSearchType = () => {
    setSearchType(searchType === 'passage' ? 'word' : 'passage');
    setSearchResults([]);
    setSearchQuery('');
    setCopiedId(null);
  };

  // Copy verse and reference to clipboard
  const copyToClipboard = (title, description, id) => {
    const textToCopy = `${title}: ${description}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopiedId(id); // Show "Copied!" for this specific verse
        setTimeout(() => setCopiedId(null), 2000); // Clear feedback after 2 seconds
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        setError('Failed to copy to clipboard');
      });
  };

  return (
    <div className="verse-search">
      <h1>ESV Bible Search</h1>
      <div className="search-container">
        <button className="search-type-btn" onClick={toggleSearchType}>
          Switch to {searchType === 'passage' ? 'Word' : 'Passage'} Search
        </button>
        <input
          type="text"
          placeholder={
            searchType === 'passage'
              ? 'Search by reference (e.g., John 3:16)...'
              : 'Search by word (e.g., love)...'
          }
          value={searchQuery}
          onChange={handleSearch}
          className="search-input"
        />
      </div>
      <div className="results-container">
        {isLoading ? (
          <p>Loading...</p>
        ) : error ? (
          <p>{error}</p>
        ) : searchQuery === '' ? (
          <p>
            Enter a {searchType === 'passage' ? 'Bible reference' : 'word'} to search
          </p>
        ) : searchResults.length > 0 ? (
          <ul className="results-list">
            {searchResults.map((item) => (
              <li key={item.id} className="result-item">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="copy-button-container">
                  <button
                    onClick={() => copyToClipboard(item.title, item.description, item.id)}
                    className="copy-button"
                  >
                    {copiedId === item.id ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No results found</p>
        )}
      </div>
    </div>
  );
}

export default VerseSearch;